import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { ReactNode, useId, useRef } from "react";
import { BaseUser } from "src/stores/models/user";
import { FieldLabel } from "./FieldLabel";
import { invalidControlSx } from "./invalidControlSx";
import { UserSelect } from "./UserSelect";

export type InputValue = string | number | boolean | string[] | null;

type InputField = {
  type:
    | "text"
    | "textarea"
    | "date"
    | "number"
    | "select"
    | "multi_select"
    | "checkbox"
    | "radio"
    | "user"
    | "file";
  label: string;
  tooltip?: string;
  placeholder?: string;
  options?: { value: string; label: string }[];
};

type FieldInputProps = {
  field: InputField;
  value: InputValue | undefined;
  users: BaseUser[];
  selectedUser: BaseUser | undefined;
  savedAttachments?: ReactNode;
  files: File[];
  existingFiles: { id: string; filename: string }[];
  onChange: (value: InputValue) => void;
  onFiles: (files: File[]) => void;
  disabled: boolean;
  readOnly?: boolean;
  required: boolean;
  outstanding?: string;
  invalid?: boolean;
};

type CommonInputProps = Pick<
  FieldInputProps,
  | "field"
  | "value"
  | "disabled"
  | "required"
  | "outstanding"
  | "invalid"
  | "onChange"
>;

const TextInput = ({
  field,
  value,
  disabled,
  required,
  outstanding,
  invalid,
  onChange,
}: CommonInputProps) => {
  const id = useId();
  const multiline = field.type === "textarea";
  const isDate = field.type === "date";
  const inputType = isDate || field.type === "number" ? field.type : "text";
  const inputValue =
    typeof value === "string" || typeof value === "number" ? value : "";

  return (
    <TextField
      id={id}
      fullWidth
      size="small"
      disabled={disabled}
      label={
        <FieldLabel
          edit
          label={field.label}
          required={required}
          outstanding={outstanding}
        />
      }
      helperText={field.tooltip}
      sx={invalid ? invalidControlSx : undefined}
      inputProps={{
        "aria-label": field.label,
        "aria-invalid": invalid || undefined,
      }}
      placeholder={field.placeholder}
      multiline={multiline}
      minRows={multiline ? 3 : undefined}
      type={inputType}
      InputLabelProps={{
        required: false,
        ...(isDate && { shrink: true }),
      }}
      value={inputValue}
      onChange={(event) => {
        const next = event.target.value;
        onChange(
          field.type === "number"
            ? next === ""
              ? null
              : Number(next)
            : isDate
              ? next || null
              : next
        );
      }}
    />
  );
};

const SelectInput = ({
  field,
  value,
  disabled,
  required,
  outstanding,
  invalid,
  onChange,
}: CommonInputProps) => {
  const id = useId();
  const multiple = field.type === "multi_select";
  const selectedValue = multiple
    ? Array.isArray(value)
      ? value
      : []
    : typeof value === "string"
      ? value
      : "";

  return (
    <TextField
      id={id}
      fullWidth
      size="small"
      select
      disabled={disabled}
      label={
        <FieldLabel
          edit
          label={field.label}
          required={required}
          outstanding={outstanding}
        />
      }
      helperText={field.tooltip}
      sx={invalid ? invalidControlSx : undefined}
      InputLabelProps={{ required: false }}
      SelectProps={{
        multiple,
        SelectDisplayProps: {
          "aria-required": required,
          "aria-invalid": invalid || undefined,
          "aria-label": field.label,
          "aria-labelledby": undefined,
        },
      }}
      value={selectedValue}
      onChange={(event) =>
        onChange(event.target.value === "" ? null : event.target.value)
      }
    >
      {!multiple && (
        <MenuItem value="">
          <em>Not selected</em>
        </MenuItem>
      )}
      {field.options?.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </TextField>
  );
};

const ChoiceInput = ({
  field,
  value,
  disabled,
  required,
  outstanding,
  invalid,
  onChange,
}: CommonInputProps) => {
  const id = useId();
  const checkbox = field.type === "checkbox";
  return (
    <FormControl disabled={disabled} required={required}>
      {checkbox ? (
        <FormControlLabel
          label={
            <FormLabel component="span" required={false}>
              <FieldLabel
                edit
                label={field.label}
                required={required}
                outstanding={outstanding}
              />
            </FormLabel>
          }
          control={
            <Checkbox
              inputProps={{
                "aria-describedby": `${id}-helper-text`,
                "aria-label": field.label,
                "aria-invalid": invalid || undefined,
              }}
              sx={
                invalid
                  ? {
                      "&:not(.Mui-checked)": { color: "error.main" },
                      "&.Mui-checked": { color: "primary.main" },
                      "&.Mui-disabled:not(.Mui-checked)": {
                        color: "rgba(211, 47, 47, 0.5)",
                      },
                    }
                  : undefined
              }
              checked={value === true}
              onChange={(_, checked) => onChange(checked)}
            />
          }
        />
      ) : (
        <>
          <FormLabel id={id} required={false}>
            <FieldLabel
              edit
              label={field.label}
              required={required}
              outstanding={outstanding}
            />
          </FormLabel>
          <RadioGroup
            row
            aria-labelledby={id}
            aria-label={field.label}
            aria-required={required}
            aria-invalid={invalid || undefined}
            aria-describedby={`${id}-helper-text`}
            value={typeof value === "string" ? value : ""}
            onChange={(_, next) => onChange(next)}
          >
            {field.options?.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={
                  <Radio
                    sx={
                      invalid
                        ? {
                            "&:not(.Mui-checked)": { color: "error.main" },
                            "&.Mui-checked": { color: "primary.main" },
                            "&.Mui-disabled:not(.Mui-checked)": {
                              color: "rgba(211, 47, 47, 0.5)",
                            },
                          }
                        : undefined
                    }
                  />
                }
                label={option.label}
              />
            ))}
          </RadioGroup>
        </>
      )}
      <FormHelperText id={`${id}-helper-text`}>{field.tooltip}</FormHelperText>
    </FormControl>
  );
};

const UserInput = ({
  field,
  users,
  selectedUser,
  disabled,
  required,
  outstanding,
  invalid,
  onChange,
}: CommonInputProps & Pick<FieldInputProps, "users" | "selectedUser">) => {
  const id = useId();
  return (
    <UserSelect
      id={id}
      fullWidth
      label={
        <FieldLabel
          edit
          label={field.label}
          required={required}
          outstanding={outstanding}
        />
      }
      helperText={field.tooltip}
      sx={invalid ? invalidControlSx : undefined}
      InputLabelProps={{ required: false }}
      SelectProps={{
        SelectDisplayProps: {
          "aria-required": required,
          "aria-invalid": invalid || undefined,
          "aria-label": field.label,
          "aria-labelledby": undefined,
        },
      }}
      disabled={disabled}
      value={selectedUser}
      users={users}
      onChange={(user) => onChange(user?.id ?? null)}
    />
  );
};

const FileInput = ({
  field,
  value,
  savedAttachments,
  files,
  existingFiles,
  disabled,
  readOnly,
  required,
  outstanding,
  invalid,
  onChange,
  onFiles,
}: FieldInputProps) => {
  const id = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const selectedFileCount = files.length;
  const attachedFileCount = Array.isArray(value) ? value.length : 0;
  const status = selectedFileCount
    ? `${selectedFileCount} file${selectedFileCount === 1 ? "" : "s"} selected`
    : attachedFileCount
      ? `${attachedFileCount} file${attachedFileCount === 1 ? "" : "s"} attached`
      : "No files selected";
  const attachmentRows = [
    ...(Array.isArray(value)
      ? value.map((fileId, index) => ({
          key: fileId,
          name:
            existingFiles.find((file) => file.id === fileId)?.filename ??
            `Saved attachment ${index + 1}`,
          label: `Remove saved ${field.label} attachment ${index + 1}`,
          remove: () => onChange(value.filter((id) => id !== fileId)),
        }))
      : []),
    ...files.map((file, index) => ({
      key: `${file.name}-${index}`,
      name: file.name,
      label: `Remove ${file.name}`,
      remove: () =>
        onFiles(files.filter((_, fileIndex) => fileIndex !== index)),
    })),
  ];

  return (
    <div className="flex flex-col gap-2">
      <FormLabel htmlFor={id} required={false}>
        <FieldLabel
          edit
          label={field.label}
          required={required}
          outstanding={outstanding}
        />
      </FormLabel>
      <input
        ref={fileInputRef}
        id={id}
        aria-label={field.label}
        aria-required={required}
        aria-invalid={invalid || undefined}
        aria-describedby={field.tooltip ? `${id}-helper-text` : undefined}
        disabled={disabled}
        type="file"
        multiple
        hidden
        onChange={(event) => {
          onFiles([...files, ...Array.from(event.target.files ?? [])]);
          event.target.value = "";
        }}
      />
      <div className="flex items-center gap-2">
        <Button
          type="button"
          size="small"
          variant="outlined"
          disabled={disabled}
          onClick={() => fileInputRef.current?.click()}
        >
          Choose files
        </Button>
        <Typography component="span" variant="caption" role="status">
          {status}
        </Typography>
      </div>
      {readOnly && attachedFileCount > 0
        ? savedAttachments
        : attachmentRows.map((file) => (
            <div key={file.key} className="flex items-center gap-2">
              <Typography variant="caption">{file.name}</Typography>
              <Button
                size="small"
                color="error"
                disabled={disabled}
                aria-label={file.label}
                onClick={file.remove}
              >
                Remove
              </Button>
            </div>
          ))}
      {field.tooltip && (
        <FormHelperText
          id={`${id}-helper-text`}
          variant="outlined"
          disabled={disabled}
        >
          {field.tooltip}
        </FormHelperText>
      )}
    </div>
  );
};

export const FieldInput = (props: FieldInputProps) => {
  switch (props.field.type) {
    case "text":
    case "textarea":
    case "date":
    case "number":
      return <TextInput {...props} />;
    case "select":
    case "multi_select":
      return <SelectInput {...props} />;
    case "checkbox":
    case "radio":
      return <ChoiceInput {...props} />;
    case "user":
      return <UserInput {...props} />;
    case "file":
      return <FileInput {...props} />;
  }
};
